import { Card, IconWrapper, Select, TextInput } from "@caprikorn/dynamo-ui";
import { classNames } from "@caprikorn/dynamo-utils";
import { type FC, useId } from "react";
import { Controller, useWatch } from "react-hook-form";

import { CircleCheck, Meter } from "@icons";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./PodInfoCard.module.css";
import type { PodInfoCardProps } from "./PodInfoCard.types";
import type { AddPodFormData } from "../AddPodForm/AddPodForm.types";
import { getIsFilled } from "../AddPodForm/AddPodForm.utils";
import formClasses from "../form.module.css";

const podInfoFields: Array<keyof AddPodFormData> = [
  "podSerial",
  "podType",
  "podDistributionSystemOperator",
  "siteAddress",
  "siteZipCode",
  "siteCity",
];

const podTypeOptions = {
  ELEC: "Linky",
  GAS: "Gazpar",
  NOT_COMMUNICATING: "Non communicant",
};

export const PodInfoCard: FC<PodInfoCardProps> = ({
  errors,
  responseData,
  control,
}) => {
  const { t } = useCustomTranslation(["main", "common"]);
  const cardId = useId();

  const [
    podSerial,
    podType,
    podDistributionSystemOperator,
    siteAddress,
    siteZipCode,
    siteCity,
  ] = useWatch({
    control,
    name: [
      "podSerial",
      "podType",
      "podDistributionSystemOperator",
      "siteAddress",
      "siteZipCode",
      "siteCity",
    ],
  });

  const energyType = useWatch({
    control,
    name: "energyType",
  });

  const isFilled = getIsFilled(podInfoFields, errors, {
    podSerial,
    podType,
    podDistributionSystemOperator,
    siteAddress,
    siteZipCode,
    siteCity,
  });

  return (
    <Card aria-labelledby={cardId} as="section" className={formClasses.card}>
      <h3
        className={classNames(formClasses.titleContainer, "font-title-md")}
        id={cardId}
      >
        {isFilled ? (
          <IconWrapper
            className={formClasses.successIcon}
            role="presentation"
            size="lg"
            src={CircleCheck}
          />
        ) : (
          <IconWrapper role="presentation" size="md" src={Meter} />
        )}
        {t("addPodDrawer.podInfo.title")}
      </h3>
      <div className={classes.formContainer}>
        <div className={classes.inputContainer}>
          <Controller
            control={control}
            name="podSerial"
            render={({ field }) => (
              <TextInput
                {...field}
                className={formClasses.input}
                disabled
                label={t("addPodDrawer.podInfo.fields.podSerial")}
                size="lg"
              />
            )}
          />
          <Controller
            control={control}
            name="podType"
            render={({ field, fieldState }) => (
              <Select
                {...field}
                disabled={Boolean(responseData)}
                isMultiple={false}
                label={t("addPodDrawer.podInfo.fields.podType.label")}
                size="lg"
                value={field.value === "" ? null : field.value}
              >
                <Select.Trigger
                  hasError={Boolean(fieldState.error)}
                  hint={
                    fieldState.error?.message &&
                    t(fieldState.error.message, { ns: "common" })
                  }
                />
                <Select.Dropdown>
                  <Select.Options>
                    <Select.Option value={podTypeOptions[energyType || "ELEC"]}>
                      {t(
                        `addPodDrawer.podInfo.fields.podType.${energyType || "ELEC"}`,
                      )}
                    </Select.Option>
                    <Select.Option value={podTypeOptions.NOT_COMMUNICATING}>
                      {t(
                        "addPodDrawer.podInfo.fields.podType.nonCommunicating",
                      )}
                    </Select.Option>
                  </Select.Options>
                </Select.Dropdown>
              </Select>
            )}
            rules={{ required: "form.error.required" }}
          />
          <Controller
            control={control}
            name="podDistributionSystemOperator"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                className={formClasses.input}
                disabled={Boolean(responseData)}
                hasError={Boolean(fieldState.error)}
                hint={
                  fieldState.error?.message &&
                  t(fieldState.error.message, { ns: "common" })
                }
                label={t(
                  "addPodDrawer.podInfo.fields.podDistributionSystemOperator",
                )}
                size="lg"
              />
            )}
            rules={{ required: "form.error.required" }}
          />
        </div>
        <div className={classes.inputContainer}>
          <Controller
            control={control}
            name="siteAddress"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                className={formClasses.input}
                disabled={Boolean(responseData)}
                hasError={Boolean(fieldState.error)}
                hint={
                  fieldState.error?.message &&
                  t(fieldState.error.message, { ns: "common" })
                }
                label={t("addPodDrawer.podInfo.fields.address")}
                size="lg"
              />
            )}
            rules={{ required: "form.error.required" }}
          />

          <Controller
            control={control}
            name="siteZipCode"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                className={formClasses.input}
                disabled={Boolean(responseData)}
                hasError={Boolean(fieldState.error)}
                hint={
                  fieldState.error?.message &&
                  t(fieldState.error.message, { ns: "common" })
                }
                label={t("addPodDrawer.podInfo.fields.zipcode")}
                size="lg"
              />
            )}
            rules={{ required: "form.error.required" }}
          />

          <Controller
            control={control}
            name="siteCity"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                className={formClasses.input}
                disabled={Boolean(responseData)}
                hasError={Boolean(fieldState.error)}
                hint={
                  fieldState.error?.message &&
                  t(fieldState.error.message, { ns: "common" })
                }
                label={t("addPodDrawer.podInfo.fields.city")}
                size="lg"
              />
            )}
            rules={{ required: "form.error.required" }}
          />
        </div>
        <div className={classes.inputContainer}>
          <Controller
            control={control}
            name="additionalAddress"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                className={formClasses.input}
                disabled={Boolean(responseData)}
                hasError={Boolean(fieldState.error)}
                hint={
                  fieldState.error?.message &&
                  t(fieldState.error.message, { ns: "common" })
                }
                label={t("addPodDrawer.podInfo.fields.additionalAddress")}
                size="lg"
              />
            )}
          />
        </div>
      </div>
    </Card>
  );
};
